import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  BlockStack,
  Badge,
  Button,
  DataTable,
  SkeletonPage,
  SkeletonBodyText,
  Banner,
  Icon,
  TextField,
  Pagination,
  Text,
  InlineStack,
  Thumbnail,
} from '@shopify/polaris';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import { PageEntrance } from '../components/ui';
import { AcpProductTable } from '../components/AcpProductTable';
import type { AcpSortKey } from '../components/AcpProductTable';
import { UcpProductTable } from '../components/UcpProductTable';
import type { UcpSortKey } from '../components/UcpProductTable';
import './ProductListPage.css';
import { getScoreTierConfig } from '../theme';
import { ExternalIcon, SearchIcon } from '@shopify/polaris-icons';
import { formatRelativeOrDate } from '../utils/dateFormat';

/**
 * ProductListPage.
 * Shows all scored products in a table with score, tier, barcode status.
 * Supports server-side sorting, search, and pagination (5 per page).
 */

const PAGE_SIZE = 20;

type SortKey = 'title_asc' | 'title_desc' | 'score_asc' | 'score_desc' | 'updated_asc' | 'updated_desc';

// Column index → [asc sort key, desc sort key]
// Columns: 0=Product, 1=Score, 2=Tier, 3=GTIN, 4=Images, 5=Last Scored, 6=Store
const COLUMN_SORT_KEYS: Record<number, [SortKey, SortKey]> = {
  0: ['title_asc', 'title_desc'],
  1: ['score_asc', 'score_desc'],
  2: ['score_asc', 'score_desc'], // Tier derives from score
  5: ['updated_asc', 'updated_desc'],
};

interface ProductListItem {
  id: string;
  shopifyProductId: string;
  title: string;
  overallScore: number;
  acpScore: number | null;
  ucpScore: number | null;
  grade: string;
  colour: string;
  imageCount: number;
  featuredImageUrl: string | null;
  barcodeStatus: string;
  lastScoredAt: string | null;
  storefrontUrl: string | null;
}

interface ProductListResponse {
  data: ProductListItem[];
  meta: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
}

function buildProductsUrl(page: number, sortBy: SortKey, search: string, acpScoreMin?: number, acpScoreMax?: number, ucpScoreMin?: number, ucpScoreMax?: number): string {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(PAGE_SIZE),
    sortBy,
  });
  if (search.trim()) params.set('search', search.trim());
  if (acpScoreMin !== undefined) params.set('acpScoreMin', String(acpScoreMin));
  if (acpScoreMax !== undefined) params.set('acpScoreMax', String(acpScoreMax));
  if (ucpScoreMin !== undefined) params.set('ucpScoreMin', String(ucpScoreMin));
  if (ucpScoreMax !== undefined) params.set('ucpScoreMax', String(ucpScoreMax));
  return `/products?${params.toString()}`;
}

function useProducts(page: number, sortBy: SortKey, search: string, acpScoreMin?: number, acpScoreMax?: number, ucpScoreMin?: number, ucpScoreMax?: number) {
  return useQuery<ProductListResponse>({
    queryKey: ['products', page, sortBy, search, acpScoreMin, acpScoreMax, ucpScoreMin, ucpScoreMax],
    queryFn: () => api.get<ProductListResponse>(buildProductsUrl(page, sortBy, search, acpScoreMin, acpScoreMax, ucpScoreMin, ucpScoreMax)),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

function barcodeStatusBadge(status: string): { label: string; tone: 'success' | 'warning' | 'critical' } {
  switch (status) {
    case 'VALID': return { label: 'Valid', tone: 'success' };
    case 'INVALID': return { label: 'Invalid', tone: 'critical' };
    default: return { label: 'Missing', tone: 'warning' };
  }
}

function getScoreFilterLabel(scoreMin?: number, scoreMax?: number): string {
  if (scoreMin === 80 && scoreMax === 100) return 'ChatGPT: Discoverable (80–100)';
  if (scoreMin === 60 && scoreMax === 79) return 'ChatGPT: Readable (60–79)';
  if (scoreMin === 40 && scoreMax === 59) return 'ChatGPT: Confusing (40–59)';
  if (scoreMin === 0 && scoreMax === 39) return 'ChatGPT: Invisible (0–39)';
  return `ChatGPT score ${scoreMin ?? 0}–${scoreMax ?? 100}`;
}

function getUcpScoreFilterLabel(scoreMin?: number, scoreMax?: number): string {
  if (scoreMin === 80 && scoreMax === 100) return 'Gemini: Discoverable (80\u2013100)';
  if (scoreMin === 60 && scoreMax === 79) return 'Gemini: Readable (60\u201379)';
  if (scoreMin === 40 && scoreMax === 59) return 'Gemini: Confusing (40\u201359)';
  if (scoreMin === 0 && scoreMax === 39) return 'Gemini: Invisible (0\u201339)';
  return `Gemini score ${scoreMin ?? 0}\u2013${scoreMax ?? 100}`;
}

export function ProductListPage(): React.ReactElement {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read ACP score range filter from URL
  const rawAcpScoreMin = searchParams.get('acpScoreMin');
  const rawAcpScoreMax = searchParams.get('acpScoreMax');
  const urlScoreMin = rawAcpScoreMin === null ? undefined : Number(rawAcpScoreMin);
  const urlScoreMax = rawAcpScoreMax === null ? undefined : Number(rawAcpScoreMax);

  // Read UCP score range filter from URL
  const rawUcpScoreMin = searchParams.get('ucpScoreMin');
  const rawUcpScoreMax = searchParams.get('ucpScoreMax');
  const urlUcpScoreMin = rawUcpScoreMin === null ? undefined : Number(rawUcpScoreMin);
  const urlUcpScoreMax = rawUcpScoreMax === null ? undefined : Number(rawUcpScoreMax);

  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<SortKey>('score_asc');
  const [acpSortBy, setAcpSortBy] = useState<AcpSortKey>('score_asc');
  const [ucpSortBy, setUcpSortBy] = useState<UcpSortKey>('score_asc');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isAcpFilter = urlScoreMin !== undefined || urlScoreMax !== undefined;
  const isUcpFilter = urlUcpScoreMin !== undefined || urlUcpScoreMax !== undefined;

  // Debounce search: reset page to 1 on new search
  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 350);
  }, []);

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const handleClearScoreFilter = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('acpScoreMin');
      next.delete('acpScoreMax');
      next.delete('ucpScoreMin');
      next.delete('ucpScoreMax');
      return next;
    });
    setPage(1);
  }, [setSearchParams]);

  const activeSortBy = isUcpFilter ? ucpSortBy : isAcpFilter ? acpSortBy : sortBy;
  const { data, isLoading, isFetching, error } = useProducts(page, activeSortBy, debouncedSearch, urlScoreMin, urlScoreMax, urlUcpScoreMin, urlUcpScoreMax);

  // Polaris DataTable sort direction
  const sortDirection = sortBy.endsWith('_asc') ? 'ascending' : 'descending';
  const sortColumnIndex = Object.entries(COLUMN_SORT_KEYS).find(
    ([, [asc, desc]]) => asc === sortBy || desc === sortBy,
  )?.[0];
  const initialSortColumnIndex = sortColumnIndex === undefined ? undefined : Number(sortColumnIndex);

  const handleSort = useCallback(
    (headingIndex: number, direction: 'ascending' | 'descending') => {
      const keys = COLUMN_SORT_KEYS[headingIndex];
      if (!keys) return;
      const newSort = direction === 'ascending' ? keys[0] : keys[1];
      setSortBy(newSort);
      setPage(1);
    },
    [],
  );

  if (isLoading) {
    return (
      <SkeletonPage title="Products">
        <Layout>
          <Layout.Section>
            <Card><SkeletonBodyText lines={10} /></Card>
          </Layout.Section>
        </Layout>
      </SkeletonPage>
    );
  }

  if (error || !data) {
    return (
      <Page title="Products" backAction={{ content: 'Dashboard', onAction: () => { navigate('/dashboard'); } }}>
        <Banner tone="critical">
          <p>{error?.message ?? 'Unable to load products.'}</p>
        </Banner>
      </Page>
    );
  }

  const { total, totalPages } = data.meta;

  const productIds = data.data.map((p) => p.id);

  const rows = data.data.map((product, index) => {
    const barcode = barcodeStatusBadge(product.barcodeStatus);
    const tierConfig = getScoreTierConfig(product.overallScore);
    return [
      <div key={`product-${product.id}`} className="prodlist-product-cell">
        <div className="prodlist-product-thumb-wrap">
          <Thumbnail
            source={product.featuredImageUrl ?? 'placeholder'}
            alt=""
            size="extraSmall"
          />
        </div>
        <Button
          key={`title-${product.id}`}
          variant="plain"
          onClick={() => navigate(`/products/${product.id}`, { state: { productIds, currentIndex: index } })}
        >
          {product.title}
        </Button>
      </div>,
      product.overallScore,
      <Badge key={`tier-${product.id}`} tone={tierConfig.badgeTone}>
        {tierConfig.label}
      </Badge>,
      <Badge key={`barcode-${product.id}`} tone={barcode.tone}>
        {barcode.label}
      </Badge>,
      product.imageCount,
      product.lastScoredAt
        ? formatRelativeOrDate(product.lastScoredAt)
        : '—',
      product.storefrontUrl ? (
        <a
          key={`store-${product.id}`}
          href={product.storefrontUrl}
          target="_blank"
          rel="noopener noreferrer"
          title="View on Store"
        >
          <Icon source={ExternalIcon} tone="interactive" />
        </a>
      ) : '—',
    ];
  });

  const startItem = (page - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(page * PAGE_SIZE, total);

  return (
    <PageEntrance>
      <Page
        title="Products"
        backAction={{ content: 'Dashboard', onAction: () => { navigate('/dashboard'); } }}
        subtitle={`${total} product${total === 1 ? '' : 's'} scored`}
      >
        <BlockStack gap="400">
          <div className="z-hero">
            <div className="z-hero-content">
              <h2 className="z-hero-title">📦 Your AI-scored product catalog</h2>
              <p className="z-hero-subtitle">
                Review every product's AI discoverability score, tier, and identifier coverage — click any product to drill into its full report.
              </p>
            </div>
          </div>
          <Layout>
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  {/* Active score range filter banner */}
                  {isUcpFilter ? (
                    <Banner tone="info" onDismiss={handleClearScoreFilter}>
                      <Text as="span" variant="bodySm">Filtering by: <strong>{getUcpScoreFilterLabel(urlUcpScoreMin, urlUcpScoreMax)}</strong></Text>
                    </Banner>
                  ) : (urlScoreMin !== undefined || urlScoreMax !== undefined) ? (
                    <Banner tone="info" onDismiss={handleClearScoreFilter}>
                      <Text as="span" variant="bodySm">Filtering by: <strong>{getScoreFilterLabel(urlScoreMin, urlScoreMax)}</strong></Text>
                    </Banner>
                  ) : null}
                  {/* Search bar */}
                  <div className="prodlist-filter-row">
                    <div className="prodlist-search-field">
                      <TextField
                        label=""
                        labelHidden
                        placeholder="Search products by name…"
                        value={searchInput}
                        onChange={handleSearchChange}
                        prefix={<Icon source={SearchIcon} tone="subdued" />}
                        clearButton
                        onClearButtonClick={() => handleSearchChange('')}
                        autoComplete="off"
                      />
                    </div>
                    {isFetching && !isLoading && (
                      <span className="prodlist-fetching-indicator">Updating…</span>
                    )}
                  </div>

                  {/* Table — UCP view, ACP view, or standard view depending on active filter */}
                  {isUcpFilter ? (
                    <UcpProductTable
                      products={data.data}
                      productIds={productIds}
                      total={total}
                      page={page}
                      pageSize={PAGE_SIZE}
                      totalPages={totalPages}
                      sortBy={ucpSortBy}
                      onSort={(s) => { setUcpSortBy(s); setPage(1); }}
                      onPageChange={setPage}
                      emptyText={debouncedSearch ? `No products found matching "${debouncedSearch}".` : 'No products found for this filter.'}
                    />
                  ) : isAcpFilter ? (
                    <AcpProductTable
                      products={data.data}
                      productIds={productIds}
                      total={total}
                      page={page}
                      pageSize={PAGE_SIZE}
                      totalPages={totalPages}
                      sortBy={acpSortBy}
                      onSort={(s) => { setAcpSortBy(s); setPage(1); }}
                      onPageChange={setPage}
                      emptyText={debouncedSearch ? `No products found matching "${debouncedSearch}".` : 'No products found for this filter.'}
                    />
                  ) : (
                    <>
                      {/* Standard table */}
                      <div className="prodlist-table-wrapper">
                        <DataTable
                          columnContentTypes={['text', 'numeric', 'text', 'text', 'numeric', 'text', 'text']}
                          headings={['Product', 'Score', 'Tier', 'GTIN', 'Images', 'Last Scored', 'Store']}
                          rows={rows}
                          sortable={[true, true, true, false, false, true, false]}
                          defaultSortDirection={sortDirection}
                          initialSortColumnIndex={initialSortColumnIndex}
                          onSort={handleSort}
                        />
                      </div>

                      {/* Pagination footer */}
                      {total > 0 && (
                        <div className="prodlist-pagination-row">
                          <InlineStack align="center" blockAlign="center" gap="400">
                            <Text as="span" variant="bodySm" tone="subdued">
                              {total === 0 ? 'No products' : `${startItem}–${endItem} of ${total}`}
                            </Text>
                            <Pagination
                              hasPrevious={page > 1}
                              onPrevious={() => setPage((p) => Math.max(1, p - 1))}
                              hasNext={page < totalPages}
                              onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
                              label={`Page ${page} of ${totalPages}`}
                            />
                          </InlineStack>
                        </div>
                      )}

                      {total === 0 && (
                        <div className="prodlist-empty-state">
                          <Text as="p" variant="bodyMd" tone="subdued">
                            {debouncedSearch ? `No products found matching "${debouncedSearch}".` : 'No products scored yet.'}
                          </Text>
                        </div>
                      )}
                    </>
                  )}
                </BlockStack>
              </Card>
            </Layout.Section>
          </Layout>
        </BlockStack>
      </Page>
    </PageEntrance>
  );
}
